import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaRegHeart,
  FaStar,
  FaWifi,
  FaVideo,
  FaCoffee,
  FaPenSquare,
  FaUsers,
  FaCalendarAlt,
  FaFilter,
  FaSnowflake,
  FaParking,
  FaBolt,
} from "react-icons/fa";
import { MdCastForEducation } from "react-icons/md";
import Navbar from "../../components/Navbar";
import LoginModal from "../../components/LoginModal";
import { roomsAPI } from "../../services/meetingsAPI";
import { getImageUrl, getExternalImageProps } from "../../utils/imageHelper";
import { filterMeetingRoomsVisibleToUser } from "../../utils/meetingRoomVisibility";
import "./MeetingSearchResults.css";
import "./MeetingRooms.css";

const ROOM_TYPE_OPTIONS = [
  "Executive Suite",
  "Training Room",
  "Boardroom",
  "Conference Hall",
  "Seminar Hall",
];

const AMENITY_FILTER_OPTIONS = [
  { id: "Wi-Fi", Icon: FaWifi },
  { id: "Air Conditioning", Icon: FaSnowflake },
  { id: "Projector", Icon: MdCastForEducation },
  { id: "Whiteboard", Icon: FaPenSquare },
  { id: "Video Conferencing", Icon: FaVideo },
  { id: "Parking", Icon: FaParking },
  { id: "Power Backup", Icon: FaBolt },
  { id: "Coffee Station", Icon: FaCoffee },
];

const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 25000;

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1280&q=80";

const normalizeAmenity = (s) => String(s || "").toLowerCase().replace(/[-\s]/g, "");

const roomMatchesTypes = (room, selected) => {
  if (!selected.length) return true;
  const hay = `${room.roomType || ""} ${room.name || ""}`.toLowerCase();
  return selected.some((t) => {
    if (t === "Executive Suite") {
      return /executive|suite|deluxe|luxury|conference|board|seminar hall/.test(hay);
    }
    if (t === "Training Room") {
      return /training|workshop|seminar/.test(hay);
    }
    if (t === "Boardroom") {
      return /board|boardroom/.test(hay);
    }
    if (t === "Conference Hall") {
      return /conference|conference hall/.test(hay);
    }
    if (t === "Seminar Hall") {
      return /seminar|seminar hall|auditorium/.test(hay);
    }
    return hay.includes(String(t).toLowerCase());
  });
};

const roomHasAmenities = (room, required) => {
  if (!required.length) return true;
  const roomNorm = (room.amenities || []).map(normalizeAmenity);
  return required.every((req) => {
    const r = normalizeAmenity(req);
    return roomNorm.some((a) => a.includes(r) || r.includes(a) || a === r);
  });
};

const pickCity = (room) => {
  const loc = room?.location;
  if (typeof loc === "string" && loc.trim()) return loc.trim();
  if (loc && typeof loc === "object") {
    const city = loc.city || loc.town || loc.name;
    const state = loc.state || loc.region;
    const line = [city, state].filter(Boolean).join(", ");
    if (line) return line;
    if (typeof loc.address === "string" && loc.address.trim()) {
      const first = loc.address.split(",")[0]?.trim();
      if (first) return first;
    }
  }
  const c = room?.city;
  if (typeof c === "string" && c.trim()) return c.trim();
  if (room?.address && typeof room.address === "object") {
    const ac = room.address.city || room.address.town;
    if (typeof ac === "string" && ac.trim()) return ac.trim();
  }
  if (typeof room?.address === "string" && room.address.trim()) {
    const first = room.address.split(",")[0]?.trim();
    if (first) return first;
  }
  const venue = room?.venue || room?.venueName;
  if (typeof venue === "string" && venue.trim()) return venue.trim();
  return "";
};

const resolveRoomImage = (room) => {
  const firstImage = Array.isArray(room.images) && room.images.length > 0 ? room.images[0] : null;
  const candidate = firstImage || room.image || "";
  if (typeof candidate === "string" && (candidate.startsWith("http") || candidate.startsWith("data:"))) {
    return candidate;
  }
  if (candidate) return getImageUrl(candidate);
  return FALLBACK_IMG;
};

const getOwnerId = (room) => {
  if (!room) return "";
  if (room.ownerId) return String(room.ownerId);
  const uid = room.userId;
  if (!uid) return "";
  if (typeof uid === "string") return uid;
  return String(uid._id || uid.id || "");
};

const isRoomOwnedByUser = (room, user) => {
  if (!room || !user) return false;
  const currentUserId = String(user._id || user.id || "");
  const ownerId = getOwnerId(room);
  if (currentUserId && ownerId && currentUserId === ownerId) return true;

  const currentEmail = String(user.email || "").trim().toLowerCase();
  const ownerEmail = String(
    room.hostEmail || room.ownerEmail || room.userId?.email || ""
  )
    .trim()
    .toLowerCase();
  return Boolean(currentEmail && ownerEmail && currentEmail === ownerEmail);
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value._id || value.id || value);
};

const isSameCompanyRoomForUser = (room, user) => {
  const roomCompanyId = getEntityId(room?.companyId);
  const userCompanyId = getEntityId(user?.company);
  return Boolean(roomCompanyId && userCompanyId && roomCompanyId === userCompanyId);
};

const hasCompanyWorkspace = (user) =>
  Boolean(getEntityId(user?.company || user?.companyId));

const isPublicRoom = (room) => String(room?.visibility || "").toLowerCase() === "public";

/** Public rooms: owners may book their own listing (same as other guests). */
const isOwnerBookingBlocked = (room, user) => {
  if (!room || !user) return false;
  if (isPublicRoom(room)) return false;
  if (!hasCompanyWorkspace(user)) return false;
  return isRoomOwnedByUser(room, user) && !isSameCompanyRoomForUser(room, user);
};

const getRoomHighlight = (room) => {
  const cap = Number(room.capacity) || 0;
  const type = String(room.roomType || "").toLowerCase();
  const name = String(room.name || "").toLowerCase();
  if (cap >= 30) return "Large Capacity";
  if (/seminar|conference|auditorium|hall/.test(type) || /seminar|conference|hall/.test(name)) {
    return "Best for Seminars";
  }
  if (room.verified || Number(room.rating) >= 4.7) return "Popular Choice";
  if (cap >= 15) return "Team Meetings";
  return "Flexible Space";
};

const normalizeRoom = (room, index = 0, source = "api") => {
  const rawImages = Array.isArray(room?.images) ? room.images : [];
  const images = rawImages
    .map((img) => {
      if (!img) return null;
      if (typeof img === "string") {
        if (img.startsWith("http") || img.startsWith("data:")) return img;
        return getImageUrl(img);
      }
      return getImageUrl(img);
    })
    .filter(Boolean);

  const fallbackId = `${source}-${index}-${String(room?.name || room?.roomName || "room")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

  const cityRaw = pickCity(room);
  const base = {
    id: room?._id || room?.id || fallbackId,
    _id: room?._id || room?.id || fallbackId,
    name: room?.name || room?.roomName || "Meeting Room",
    userId: room?.userId || null,
    ownerId:
      room?.ownerId ||
      (typeof room?.userId === "object"
        ? room.userId?._id || room.userId?.id
        : room?.userId) ||
      "",
    hostName:
      room?.hostName ||
      room?.ownerName ||
      room?.userId?.name ||
      room?.userId?.email ||
      "Owner",
    hostEmail:
      room?.hostEmail ||
      room?.ownerEmail ||
      room?.userId?.email ||
      "",
    location: cityRaw || "Location on request",
    capacity: Number(room?.capacity) || 0,
    pricePerHour: Number(room?.pricePerHour ?? room?.price ?? room?.hourlyRate) || 0,
    description:
      room?.description ||
      "Comfortable meeting room with modern setup for productive sessions.",
    images,
    roomType: room?.roomType || "Meeting Room",
    visibility: room?.visibility === "public" ? "public" : "private",
    rating: Number(room?.rating) || 4.5,
    verified: !!room?.verified,
    amenities: Array.isArray(room?.amenities) ? room.amenities : [],
    source: room?.source || "local",
    externalLink: room?.externalLink || room?.website || null,
  };
  return { ...base, image: resolveRoomImage(base) };
};

const MeetingRooms = () => {
  const navigate = useNavigate();
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    roomType: [],
    amenities: [],
    priceRange: [0, PRICE_SLIDER_MAX],
    capacityMin: 0,
    capacityMax: 9999,
  });
  const [sortBy, setSortBy] = useState("price-low");
  const [showFilters, setShowFilters] = useState(false);

  const handleSignInClick = () => {
    if (!localStorage.getItem("authRedirectPath")) {
      localStorage.setItem("authRedirectPath", "/meetings/rooms");
    }
    setIsLoginModalOpen(true);
  };

  useEffect(() => {
    const fetchRooms = async () => {
      setLoading(true);
      try {
        const response = await roomsAPI.getRooms({ sort: "latest" });
        const apiRooms = Array.isArray(response?.data) ? response.data : [];
        const visibleRooms = filterMeetingRoomsVisibleToUser(apiRooms, currentUser);
        const normalizedApiRooms = visibleRooms.map((room, index) => normalizeRoom(room, index, "api"));

        const byId = new Map();
        normalizedApiRooms.forEach((room) => {
          if (room?.id && !byId.has(room.id)) byId.set(room.id, room);
        });

        setRooms(Array.from(byId.values()));
      } catch (error) {
        console.error("Error fetching rooms:", error);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
    window.addEventListener("meetingRoomsUpdated", fetchRooms);
    window.addEventListener("focus", fetchRooms);
    return () => {
      window.removeEventListener("meetingRoomsUpdated", fetchRooms);
      window.removeEventListener("focus", fetchRooms);
    };
  }, [currentUser]);

  const getFilteredRooms = () => {
    let filtered = [...rooms];
    if (filters.roomType.length > 0) {
      filtered = filtered.filter((r) => roomMatchesTypes(r, filters.roomType));
    }
    if (filters.amenities.length > 0) {
      filtered = filtered.filter((r) => roomHasAmenities(r, filters.amenities));
    }
    filtered = filtered.filter(
      (r) => r.pricePerHour >= filters.priceRange[0] && r.pricePerHour <= filters.priceRange[1]
    );
    filtered = filtered.filter((r) => {
      const cap = r.capacity || 0;
      if (filters.capacityMin > 0 && cap < filters.capacityMin) return false;
      if (filters.capacityMax < 9999 && cap > filters.capacityMax) return false;
      return true;
    });
    return filtered;
  };

  const getSortedRooms = (list) => {
    const sorted = [...list];
    switch (sortBy) {
      case "price-low":
        return sorted.sort((a, b) => a.pricePerHour - b.pricePerHour);
      case "price-high":
        return sorted.sort((a, b) => b.pricePerHour - a.pricePerHour);
      case "capacity-large":
        return sorted.sort((a, b) => b.capacity - a.capacity);
      case "rating":
        return sorted.sort((a, b) => b.rating - a.rating);
      default:
        return sorted;
    }
  };

  const filteredAndSortedRooms = useMemo(
    () => getSortedRooms(getFilteredRooms()),
    [rooms, filters, sortBy]
  );

  const toggleFilter = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter((item) => item !== value)
        : [...prev[filterType], value],
    }));
  };

  const setPriceRangeLow = (v) => {
    const n = Math.min(Math.max(PRICE_SLIDER_MIN, v), PRICE_SLIDER_MAX);
    setFilters((prev) => {
      const hi = prev.priceRange[1];
      const low = Math.min(n, hi);
      return { ...prev, priceRange: [low, hi] };
    });
  };

  const setPriceRangeHigh = (v) => {
    const n = Math.min(Math.max(PRICE_SLIDER_MIN, v), PRICE_SLIDER_MAX);
    setFilters((prev) => {
      const lo = prev.priceRange[0];
      const high = Math.max(n, lo);
      return { ...prev, priceRange: [lo, high] };
    });
  };

  const pageTitle = "All Meeting Rooms";

  return (
    <div className="meeting-search-results-page meeting-rooms-all-page meeting-page-with-bottom-nav page-content">
      <Navbar isScrolled={false} onSignInClick={handleSignInClick} showLoginModal />

      <div className="search-results-container">
        <div className="msr-back-row">
          <button type="button" onClick={() => navigate(-1)} className="msr-back-btn" aria-label="Go back">
            <svg className="msr-back-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        <div className="search-summary mar-page-hero">
          <div className="summary-left">
            <p className="mar-page-eyebrow">Discover & book</p>
            <h1 className="summary-title">{pageTitle}</h1>
            <p className="mar-page-subtitle">
              Browse verified meeting spaces listed by hosts — filter by capacity, price, and amenities.
            </p>
            <p className="results-count">
              {loading ? (
                "Loading rooms…"
              ) : (
                <span className="mar-results-pill">
                  {filteredAndSortedRooms.length} room{filteredAndSortedRooms.length === 1 ? "" : "s"} available
                </span>
              )}
            </p>
          </div>
          <div className="summary-right">
            <div className="summary-actions">
              <button type="button" className="map-view-btn">
                <FaMapMarkedAlt className="map-view-btn-icon" aria-hidden />
                Map
              </button>
              <button type="button" className="mobile-filter-toggle" onClick={() => setShowFilters(!showFilters)}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                  />
                </svg>
                Filters
              </button>
            </div>
            <div className="sort-bar">
              <div className="result-view-icons" aria-hidden>
                <button type="button" className="view-icon active" aria-label="Grid view">
                  ▦
                </button>
                <button type="button" className="view-icon" aria-label="List view">
                  ☰
                </button>
              </div>
              <label className="sort-label" htmlFor="mar-sort-select">
                Sort by:
              </label>
              <select
                id="mar-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="price-low">Price: Low to High</option>
                <option value="rating">Rating</option>
                <option value="capacity-large">Popularity</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>

        <div className="results-layout">
          <aside className={`filters-sidebar ${showFilters ? "show" : ""}`}>
            <div className="filters-header">
              <h2>Filters</h2>
            </div>

            <div className="filter-section">
              <h3 className="filter-title">Room Type</h3>
              {ROOM_TYPE_OPTIONS.map((type) => (
                <label key={type} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.roomType.includes(type)}
                    onChange={() => toggleFilter("roomType", type)}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>

            <div className="filter-section">
              <h3 className="filter-title">Price Range</h3>
              <div className="price-dual-wrap">
                <div className="price-dual-track" aria-hidden />
                <input
                  type="range"
                  min={PRICE_SLIDER_MIN}
                  max={PRICE_SLIDER_MAX}
                  step={100}
                  value={filters.priceRange[0]}
                  onChange={(e) => setPriceRangeLow(parseInt(e.target.value, 10))}
                  className="price-dual-input price-dual-input--low"
                  aria-label="Minimum price per hour"
                />
                <input
                  type="range"
                  min={PRICE_SLIDER_MIN}
                  max={PRICE_SLIDER_MAX}
                  step={100}
                  value={filters.priceRange[1]}
                  onChange={(e) => setPriceRangeHigh(parseInt(e.target.value, 10))}
                  className="price-dual-input price-dual-input--high"
                  aria-label="Maximum price per hour"
                />
              </div>
              <div className="price-range-scale">
                <span>₹{filters.priceRange[0].toLocaleString("en-IN")}</span>
                <span>₹{filters.priceRange[1].toLocaleString("en-IN")}/hr</span>
              </div>
            </div>

            <div className="filter-section">
              <h3 className="filter-title">Capacity</h3>
              <div className="capacity-row">
                <select
                  className="capacity-select"
                  value={
                    filters.capacityMin <= 0
                      ? "all"
                      : filters.capacityMin >= 13
                        ? "13"
                        : filters.capacityMin >= 5
                          ? "5"
                          : "1"
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      capacityMin: v === "all" ? 0 : v === "1" ? 1 : v === "5" ? 5 : 13,
                    }));
                  }}
                  aria-label="Minimum capacity"
                >
                  <option value="all">Any size</option>
                  <option value="1">1–4 People</option>
                  <option value="5">5–12 People</option>
                  <option value="13">13+ People</option>
                </select>
                <select
                  className="capacity-select"
                  value={
                    filters.capacityMax >= 9999 ? "any" : filters.capacityMax <= 4 ? "4" : filters.capacityMax <= 12 ? "12" : "50"
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      capacityMax: v === "any" ? 9999 : v === "4" ? 4 : v === "12" ? 12 : 50,
                    }));
                  }}
                  aria-label="Maximum capacity"
                >
                  <option value="any">No max</option>
                  <option value="4">Up to 4</option>
                  <option value="12">Up to 12</option>
                  <option value="50">Up to 50</option>
                </select>
              </div>
            </div>

            <div className="filter-section filter-section--amenities">
              <h3 className="filter-title">Amenities</h3>
              {AMENITY_FILTER_OPTIONS.map(({ id, Icon }) => (
                <label key={id} className="filter-checkbox filter-checkbox--amenity">
                  <input
                    type="checkbox"
                    checked={filters.amenities.includes(id)}
                    onChange={() => toggleFilter("amenities", id)}
                  />
                  <span className="filter-amenity-icon" aria-hidden>
                    <Icon />
                  </span>
                  <span>{id}</span>
                </label>
              ))}
            </div>

            <div className="filter-actions">
              <button
                type="button"
                className="clear-filters"
                onClick={() =>
                  setFilters({
                    roomType: [],
                    amenities: [],
                    priceRange: [0, PRICE_SLIDER_MAX],
                    capacityMin: 0,
                    capacityMax: 9999,
                  })
                }
              >
                Clear All
              </button>
              <button type="button" className="apply-filters-btn" onClick={() => setShowFilters(false)}>
                <FaFilter aria-hidden />
                Apply Filters
              </button>
            </div>
          </aside>

          <main className="results-main mar-rooms-main">

            {loading && (
              <div className="loading-state">
                <div className="spinner" />
                <p>Loading meeting rooms...</p>
              </div>
            )}

            {!loading && rooms.length === 0 && (
              <div className="empty-state meeting-rooms-empty-state">
                <h3>No owner-added meeting rooms found.</h3>
                <p>Owners can add rooms from Add Your Space. Once added, they appear here.</p>
                <button type="button" className="meeting-rooms-empty-cta" onClick={() => navigate("/meetings/add-your-space")}>
                  Add Your Space
                </button>
              </div>
            )}

            {!loading && rooms.length > 0 && filteredAndSortedRooms.length === 0 && (
              <div className="empty-state">
                <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3>No rooms match your filters</h3>
                <p>Try adjusting filters or clear all.</p>
              </div>
            )}

            {!loading && filteredAndSortedRooms.length > 0 && (
              <div className="mar-rooms-grid">
                {filteredAndSortedRooms.map((room) => {
                  const isOwnRoom = isOwnerBookingBlocked(room, currentUser);
                  const amenityList = room.amenities.length ? room.amenities : ["Wi-Fi", "Air Conditioning"];
                  return (
                    <article key={room.id} className="mar-room-card">
                      <div className="mar-room-card__media">
                        <img
                          src={room.image}
                          alt={room.name}
                          className="mar-room-card__img"
                          loading="lazy"
                          {...getExternalImageProps(room.source)}
                          onError={(e) => {
                            const el = e.currentTarget;
                            if (el.dataset.fallbackApplied) return;
                            el.dataset.fallbackApplied = "1";
                            el.src = FALLBACK_IMG;
                          }}
                        />
                        <span className="mar-room-card__rating">
                          <FaStar aria-hidden />
                          {Number(room.rating).toFixed(1)}
                        </span>
                        <button type="button" className="mar-room-card__fav" aria-label="Save room">
                          <FaRegHeart />
                        </button>
                        <span className="mar-room-card__highlight">{getRoomHighlight(room)}</span>
                      </div>

                      <div className="mar-room-card__body">
                        <div className="mar-room-card__head">
                          <h3 className="mar-room-card__title">{room.name}</h3>
                          <span className="mar-room-card__type-pill">{room.roomType}</span>
                        </div>
                        <p className="mar-room-card__location">
                          <FaMapMarkerAlt aria-hidden />
                          <span>{room.location}</span>
                        </p>

                        <div className="mar-room-card__meta">
                          <span className="mar-room-card__meta-chip">
                            <FaUsers aria-hidden />
                            {room.capacity || "—"} seats
                          </span>
                          <span className="mar-room-card__meta-chip mar-room-card__meta-chip--live">
                            <span className="mar-room-card__live-dot" aria-hidden />
                            Available
                          </span>
                        </div>

                        <div className="mar-room-card__amenities">
                          {amenityList.slice(0, 5).map((amenity, index) => (
                            <span key={`${room.id}-am-${index}`} className="mar-room-card__amenity">
                              {amenity}
                            </span>
                          ))}
                          {amenityList.length > 5 && (
                            <span className="mar-room-card__amenity mar-room-card__amenity--more">
                              +{amenityList.length - 5}
                            </span>
                          )}
                        </div>

                        <div className="mar-room-card__footer">
                          <div className="mar-room-card__price">
                            {room.pricePerHour > 0 ? (
                              <>
                                <span className="mar-room-card__price-value">
                                  ₹{Number(room.pricePerHour).toLocaleString("en-IN")}
                                </span>
                                <span className="mar-room-card__price-unit">/ hour</span>
                              </>
                            ) : (
                              <span className="mar-room-card__price-muted">Price on request</span>
                            )}
                          </div>
                          {["serpapi", "geoapify"].includes(room.source) && room.externalLink ? (
                            <a
                              href={room.externalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mar-room-card__cta mar-room-card__cta--primary"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaCalendarAlt aria-hidden />
                              Book now
                            </a>
                          ) : isOwnRoom ? (
                            <button
                              type="button"
                              className="mar-room-card__cta mar-room-card__cta--owned"
                              disabled
                              title="You cannot book your own room"
                            >
                              Your listing
                            </button>
                          ) : (
                            <Link
                              to={`/meetings/book-flow?roomId=${encodeURIComponent(room.id || room._id)}`}
                              className="mar-room-card__cta mar-room-card__cta--primary"
                            >
                              <FaCalendarAlt aria-hidden />
                              Book now
                            </Link>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {!loading && filteredAndSortedRooms.length > 0 && (
              <div className="results-pagination" aria-label="Pagination">
                <button type="button" className="page-btn" aria-label="Previous page">
                  ‹
                </button>
                <button type="button" className="page-btn active" aria-current="page">
                  1
                </button>
                <button type="button" className="page-btn" aria-label="Next page">
                  ›
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </div>
  );
};

export default MeetingRooms;
